import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { IAccessControlEntry, IAddAccessControlRequest, IRemoveAccessControlRequest } from './access-control.interface';

@Injectable({
  providedIn: 'root'
})
export class AccessControlService {
  private baseUrl = environment.api.baseUrl;
  private accessControlEntriesSubject = new BehaviorSubject<IAccessControlEntry[]>([]);
  public accessControlEntries$ = this.accessControlEntriesSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    const token = this.authService.token;
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  getAccessControlEntries(type?: 'Moderator' | 'AllowList'): Observable<{ accessControlEntries: IAccessControlEntry[], count: number }> {
    const headers = this.getHeaders();
    let url = `${this.baseUrl}/getAccessControl`;
    
    if (type) {
      url += `?type=${type}`;
    }

    return this.http.get<{ accessControlEntries: IAccessControlEntry[], count: number }>(url, { headers });
  }

  addAccessControlEntry(request: IAddAccessControlRequest): Observable<{ message: string, entry: IAccessControlEntry }> {
    const headers = this.getHeaders();
    return this.http.post<{ message: string, entry: IAccessControlEntry }>(
      `${this.baseUrl}/addAccessControl`, 
      request, 
      { headers }
    );
  }

  removeAccessControlEntry(request: IRemoveAccessControlRequest): Observable<{ message: string, deletedEntries: IAccessControlEntry[] }> {
    const headers = this.getHeaders();
    return this.http.delete<{ message: string, deletedEntries: IAccessControlEntry[] }>(
      `${this.baseUrl}/removeAccessControl`,
      { 
        headers,
        body: request
      }
    );
  }

  refreshAccessControlEntries(): void {
    this.getAccessControlEntries().subscribe({
      next: (response) => {
        this.accessControlEntriesSubject.next(response.accessControlEntries);
      },
      error: (error) => {
        console.error('Error fetching access control entries:', error);
      }
    });
  }

  getModerators(): Observable<{ accessControlEntries: IAccessControlEntry[], count: number }> {
    return this.getAccessControlEntries('Moderator');
  }

  getAllowListUsers(): Observable<{ accessControlEntries: IAccessControlEntry[], count: number }> {
    return this.getAccessControlEntries('AllowList');
  }
}
